import { useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useMutation } from '@apollo/client/react';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { authErrorMessage, type AuthSession } from '~utils/auth';
import { loadGoogleSdk, type CodeResponse, type CodeClientError } from './sdk';
import { LOGIN_WITH_GOOGLE_CODE } from './operations';
import { styles } from './styles';
import googleIcon from './google.svg';
type Props = {
  onLogin: (session: AuthSession) => void;
  onError: (message: string | null) => void;
};

// Web port of widgets/continue_with/google_button.dart: white, brand icon on
// the left, centered label. A custom button can't receive an id_token from
// GIS (only its rendered button can), so this starts the popup
// authorization-code flow and the backend redeems the code
// (loginWithGoogleCode) before running the usual id_token verification.
export function ContinueWithGoogle({
  onLogin,
  onError
}: Props) {
  const [loading, setLoading] = useState(false);
  const [loginWithGoogleCode] = useMutation(LOGIN_WITH_GOOGLE_CODE);
  if (!process.env.GOOGLE_WEB_CLIENT_ID) return null;
  const deps: SignInDeps = {
    loading,
    setLoading,
    loginWithGoogleCode,
    onLogin,
    onError
  };
  return <button type="button" {...stylex.props(styles.button)} onClick={() => signIn(deps)} disabled={loading}>
      <img src={googleIcon} alt="" width={20} height={20} {...stylex.props(styles.icon)} />
      {loading ? 'Signing in…' : 'Continue with Google'}
    </button>;
}
type LoginWithGoogleCodeFn = (options: {
  variables: VariablesOf<typeof LOGIN_WITH_GOOGLE_CODE>;
}) => Promise<{
  data?: ResultOf<typeof LOGIN_WITH_GOOGLE_CODE> | null;
}>;
type SignInDeps = Props & {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loginWithGoogleCode: LoginWithGoogleCodeFn;
};
const signIn = async (deps: SignInDeps) => {
  const {
    loading,
    setLoading,
    onError
  } = deps;
  if (loading) return;
  onError(null);
  setLoading(true);
  try {
    const google = await loadGoogleSdk();
    google.accounts.oauth2.initCodeClient({
      client_id: process.env.GOOGLE_WEB_CLIENT_ID,
      scope: 'openid email profile',
      ux_mode: 'popup',
      callback: response => redeemCode(deps, response),
      error_callback: error => handlePopupError(deps, error)
    }).requestCode();
  } catch {
    setLoading(false);
    onError('Google sign-in is unavailable right now.');
  }
};
const redeemCode = ({
  setLoading,
  loginWithGoogleCode,
  onLogin,
  onError
}: SignInDeps, {
  code,
  error
}: CodeResponse) => {
  if (!code || error) {
    setLoading(false);
    if (error) onError('Google sign-in failed. Please try again.');
    return;
  }
  loginWithGoogleCode({
    variables: {
      input: {
        code
      }
    }
  }).then(result => {
    if (result.data) onLogin(result.data.loginWithGoogleCode);
  }).catch((err: unknown) => onError(authErrorMessage(err))).finally(() => setLoading(false));
};
const handlePopupError = ({
  setLoading,
  onError
}: SignInDeps, {
  type
}: CodeClientError) => {
  setLoading(false);
  if (type !== 'popup_closed') {
    onError('Google sign-in failed. Please try again.');
  }
};